/**
 * TK-36 việc 2 — GIỜ CẤP ĐIỆN của tủ cho đèn NEMA, THUẦN (không React) để `kiem-tk35` chạy với đồng hồ giả và phá.
 *
 * lee 22/10: đèn CHỈ CÓ ĐIỆN khi ngõ ra cấp cho nó bật. Khung dim nằm ngoài giờ ngõ ra bật là khung không ai thấy — vẫn cho
 * gửi (người dùng có thể cố ý, vd soạn trước khi đổi lịch tủ) nhưng phải NÓI ra.
 *
 * Diễn giải lịch 48 timer ĐÚNG ngữ nghĩa firmware (bộ giả lập `tools/device-simulator/src/schedule.ts` bắt chước nó):
 *   · TIMER: timer bật + đúng NGÀY (có ngày đặc biệt ⇒ CHỈ những ngày ấy; không có ⇒ 7 cờ thứ, 0 = Chủ nhật) + `inRange(on, off)`:
 *     on < off ⇒ [on, off); on > off (qua đêm) ⇒ [on, 1440) ∪ [0, off) CÙNG ngày (firmware xét ngày hiện tại cho cả hai nửa);
 *     on = off ⇒ không bật.
 *   · SEASON: [lặn + offset, mọc + offset) theo `inRange`; chưa biết giờ mặt trời ⇒ CHƯA BIẾT (null) — luật số 0, không đoán.
 *   · ALWAYS_ON ⇒ cả ngày · ALWAYS_OFF / ngõ tắt (`enabled = false`) ⇒ không bật.
 * Giờ là giờ TỦ (Việt Nam, UTC+7, không đổi giờ mùa) — `ngayVN()` đổi từ đồng hồ máy.
 */
export type CheDo = 'TIMER' | 'SEASON' | 'ALWAYS_ON' | 'ALWAYS_OFF';
export interface TimerLich { enabled: boolean; onMin: number; offMin: number; daysOfWeek: string; specialDays: { d: number; m: number }[] }
export interface NgoLich { index: number; enabled: boolean; mode: CheDo; sunsetOffsetMin: number; sunriseOffsetMin: number; timers: TimerLich[] }
export interface MatTroi { sunsetMin: number | null; sunriseMin: number | null }
export interface NgayVN { dow: number; d: number; m: number }
/** Đoạn phút trong MỘT ngày, nửa mở [a, b), 0 ≤ a < b ≤ 1440. */
export type Doan = [number, number];

export const PHUT_NGAY = 1440;
export const LECH_VN_MS = 7 * 3_600_000;

/** Ngày + phút theo giờ Việt Nam của một mốc đồng hồ (ms epoch), lùi/tiến `themNgay`. */
export function ngayVN(ms: number, themNgay = 0): NgayVN & { phut: number } {
  const t = new Date(ms + LECH_VN_MS + themNgay * 86_400_000);
  return { dow: t.getUTCDay(), d: t.getUTCDate(), m: t.getUTCMonth() + 1, phut: t.getUTCHours() * 60 + t.getUTCMinutes() };
}

const vong = (x: number) => ((x % PHUT_NGAY) + PHUT_NGAY) % PHUT_NGAY;
/** `inRange(on, off)` của firmware thành đoạn trong ngày. */
function doanCua(on: number, off: number): Doan[] {
  if (on === off) return [];
  return on < off ? [[on, off]] : [[on, PHUT_NGAY], ...(off > 0 ? [[0, off] as Doan] : [])];
}
function dungNgay(t: TimerLich, n: NgayVN): boolean {
  const dac = t.specialDays.filter((s) => s.d > 0 && s.m > 0);
  if (dac.length) return dac.some((s) => s.d === n.d && s.m === n.m);
  return t.daysOfWeek[n.dow] === '1';
}

/** Gộp các đoạn chồng / liền nhau. */
export function gop(ds: Doan[]): Doan[] {
  const x = [...ds].filter(([a, b]) => b > a).sort((p, q) => p[0] - q[0]);
  const r: Doan[] = [];
  for (const [a, b] of x) { const cuoi = r[r.length - 1]; if (cuoi && a <= cuoi[1]) cuoi[1] = Math.max(cuoi[1], b); else r.push([a, b]); }
  return r;
}

/** Các đoạn ngõ ra BẬT trong một ngày; `null` = CHƯA BIẾT (SEASON thiếu giờ mặt trời). */
export function khoangBat(o: NgoLich, n: NgayVN, mt: MatTroi): Doan[] | null {
  if (!o.enabled || o.mode === 'ALWAYS_OFF') return [];
  if (o.mode === 'ALWAYS_ON') return [[0, PHUT_NGAY]];
  if (o.mode === 'SEASON') {
    if (mt.sunsetMin == null || mt.sunriseMin == null) return null;
    return gop(doanCua(vong(mt.sunsetMin + o.sunsetOffsetMin), vong(mt.sunriseMin + o.sunriseOffsetMin)));   // qua đêm ⇒ sắp lại (`phu` cần thứ tự)
  }
  return gop(o.timers.filter((t) => t.enabled && dungNgay(t, n)).flatMap((t) => doanCua(t.onMin, t.offMin)));
}

/** Dải CẤP ĐIỆN trong một ngày cho một tập ngõ (hợp); có ngõ chưa biết ⇒ null. Ngõ không có trong lịch ⇒ bỏ qua. */
export function daiCapDien(ngo: NgoLich[], chiSo: number[], n: NgayVN, mt: MatTroi): Doan[] | null {
  const ds: Doan[] = [];
  for (const i of chiSo) {
    const o = ngo.find((x) => x.index === i); if (!o) continue;
    const k = khoangBat(o, n, mt); if (k === null) return null;
    ds.push(...k);
  }
  return gop(ds);
}

const phu = (dai: Doan[], a: number, b: number) => {   // [a, b) có nằm trọn trong hợp `dai` không
  let x = a;
  for (const [p, q] of dai) { if (p <= x && x < q) x = q; if (x >= b) return true; }
  return x >= b;
};

export type KetKiem = 'trong' | 'ngoai' | 'chua-biet';
/**
 * Khung dim [tu, den) (phút, qua đêm được) có nằm TRỌN trong giờ cấp điện không — xét `soNgay` ngày bắt đầu từ `batDau`
 * (lịch dim lặp mỗi ngày; lịch tủ có thể khác theo thứ). Qua đêm: nửa sau xét theo NGÀY KẾ. Một phút ngoài ở bất kỳ ngày nào ⇒ 'ngoai'.
 */
export function kiemKhung(tu: number, den: number, dai: (themNgay: number) => Doan[] | null, soNgay = 7): KetKiem {
  if (tu === den) return 'trong';
  for (let i = 0; i < soNgay; i++) {
    const d0 = dai(i);
    if (d0 === null) return 'chua-biet';
    if (tu < den) { if (!phu(d0, tu, den)) return 'ngoai'; continue; }
    const d1 = dai(i + 1);
    if (d1 === null) return 'chua-biet';
    if (!phu(d0, tu, PHUT_NGAY) || (den > 0 && !phu(d1, 0, den))) return 'ngoai';
  }
  return 'trong';
}

/**
 * Giờ BẬT kế tiếp của một ngõ, tính từ `nowMs` (giờ VN), tìm trong 8 ngày. `null` = lịch không có giờ bật nào; 'chua-biet' =
 * không phán được (SEASON thiếu giờ mặt trời) — hai câu khác nhau, không gộp. Ngõ đang bật theo lịch ⇒ `dangBat`.
 */
export function batKeTiep(o: NgoLich, nowMs: number, mt: MatTroi): { themNgay: number; phut: number; dangBat: boolean } | null | 'chua-biet' {
  const h = ngayVN(nowMs);
  for (let i = 0; i <= 8; i++) {
    const k = khoangBat(o, ngayVN(nowMs, i), mt); if (k === null) return 'chua-biet';
    for (const [a, b] of k) {
      if (i === 0 && h.phut >= a && h.phut < b) return { themNgay: 0, phut: h.phut, dangBat: true };
      if (i > 0 || a > h.phut) return { themNgay: i, phut: a, dangBat: false };
    }
  }
  return null;
}

export const hhmm = (p: number) => `${String(Math.floor(p / 60) % 24).padStart(2, '0')}:${String(p % 60).padStart(2, '0')}`;
const THU = ['Chủ nhật', 'thứ 2', 'thứ 3', 'thứ 4', 'thứ 5', 'thứ 6', 'thứ 7'];
/** "18:00" · "05:00 ngày mai" · "18:00 thứ 7" */
export function nhanGioBat(k: { themNgay: number; phut: number }, nowMs: number): string {
  if (k.themNgay === 0) return hhmm(k.phut);
  if (k.themNgay === 1) return `${hhmm(k.phut)} ngày mai`;
  return `${hhmm(k.phut)} ${THU[ngayVN(nowMs, k.themNgay).dow]}`;
}

/**
 * Ngõ ra cấp điện cho ĐÍCH của một khung dim: tất cả ⇒ mọi ngõ; nhóm / đèn ⇒ ngõ theo NHÁNH của đèn; có đèn chưa gán nhánh (hoặc nhóm
 * rỗng) ⇒ mọi ngõ — cùng luật `capDien` của máy chủ (nhánh NULL ⇒ có điện khi ít nhất một ngõ bật).
 */
export function ngoCuaDich(
  dich: { loai: 'tat' } | { loai: 'nhom'; groupId: string } | { loai: 'den'; addr: number },
  den: { addr: number; groupId: string | null; nhanh: number | null }[], moiNgo: number[],
): number[] {
  const ds = dich.loai === 'tat' ? den : dich.loai === 'nhom' ? den.filter((d) => d.groupId === dich.groupId) : den.filter((d) => d.addr === dich.addr);
  if (dich.loai === 'tat' || ds.length === 0 || ds.some((d) => d.nhanh === null)) return moiNgo;
  return [...new Set(ds.map((d) => d.nhanh as number))].sort((a, b) => a - b);
}
